import React from 'react';
import { UnifiedStream } from '../types';
import { TwitchIcon, YouTubeIcon, ViewersIcon, ClockIcon, ExternalLinkIcon } from './Icons';

interface StreamCardProps {
  stream: UnifiedStream;
}

export const StreamCard: React.FC<StreamCardProps> = ({ stream }) => {
  const formatViewerCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const formatUptime = (startedAt: string): string => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins}m`;
    }
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    if (hours < 24) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const PlatformIcon = stream.platform === 'twitch' ? TwitchIcon : YouTubeIcon;

  return (
    <a
      href={stream.streamUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="stream-card"
    >
      <div className="stream-thumbnail">
        <img
          src={stream.thumbnailUrl}
          alt={`${stream.streamerName}'s stream`}
          loading="lazy"
        />
        <div className="stream-badges">
          <span className={`stream-platform ${stream.platform}`}>
            <PlatformIcon />
            {stream.platform}
          </span>
          <span className="stream-live-badge">
            <span className="live-dot" />
            Live
          </span>
        </div>
        <div className="stream-stats">
          <span className="stream-viewers">
            <ViewersIcon />
            {formatViewerCount(stream.viewerCount)}
          </span>
          <span className="stream-uptime">
            <ClockIcon />
            {formatUptime(stream.startedAt)}
          </span>
        </div>
      </div>
      <div className="stream-info">
        <h3 className="stream-title">{stream.title}</h3>
        <div className="stream-meta">
          <p className="stream-streamer">{stream.streamerName}</p>
          <ExternalLinkIcon />
        </div>
      </div>
    </a>
  );
};

export const StreamCardSkeleton: React.FC = () => (
  <div className="stream-card stream-card-skeleton">
    <div className="stream-thumbnail skeleton" />
    <div className="stream-info">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-meta" />
    </div>
  </div>
);
